import React from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';

import { PermissionsPrompt } from '@sg/shared-fe-features-permissions';

import { useLocationPermissionPrimer, usePushNotificationPermissionPrimer } from '../../hooks';
import { PermissionsPrimerLocationView, PermissionsPrimerPushView } from './components';
import { usePermissionsPrimerAnimations } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsPrimer = (props: Props) => {
  const { pushPrimer, locationPrimer } = props;

  // ─── Phase ───────────────────────────────────────────────────────────

  const isPushPhase = pushPrimer.isPrimerActive;
  const isPushRequesting = pushPrimer.isRequestingPushNotificationPermission;

  // ─── Animations ──────────────────────────────────────────────────────

  const {
    hexagonScaleSV,
    hexagonRotateSV,
    appIconOpacitySV,
    contentOpacitySV,
    hexagonAnchorAnimatedStyles,
    hexagonEntering,
    hexagonLogoEntering,
    appIconEntering,
    contentEnteringDelay,
    locationStrokes,
  } = usePermissionsPrimerAnimations({ isPushPhase, isPushRequesting });

  const hexagonAnchorStyles = [styles.hexagonAnchor, hexagonAnchorAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <PermissionsPrompt.Container>
      <View style={StyleSheet.absoluteFill} pointerEvents="none">
        <Animated.View style={hexagonAnchorStyles}>
          <PermissionsPrompt.Hexagon
            scaleSV={hexagonScaleSV}
            rotateSV={hexagonRotateSV}
            entering={hexagonEntering}
            hideLogo={isPushPhase}
            logoEntering={hexagonLogoEntering}
          />

          {isPushPhase ? (
            <View style={styles.appIconAnchor}>
              <PermissionsPrompt.AppIcon entering={appIconEntering} opacitySV={appIconOpacitySV} />
            </View>
          ) : null}
        </Animated.View>

        {isPushPhase ? null : (
          <View style={styles.strokesContainer}>
            {locationStrokes.map((stroke, index) => (
              <PermissionsPrompt.HexagonStroke
                key={`primer-hexagon-stroke-${index}`}
                scale={stroke.scale}
                delay={stroke.delay}
                hideActiveOverlay={stroke.hideActiveOverlay}
              />
            ))}
          </View>
        )}
      </View>

      <PermissionsPrompt.ContentContainer
        enteringDelay={contentEnteringDelay}
        opacitySV={contentOpacitySV}
      >
        {isPushPhase ? (
          <PermissionsPrimerPushView
            isRequesting={pushPrimer.isRequestingPushNotificationPermission}
            isSkipping={pushPrimer.isSkippingPushNotificationPermission}
            onAllow={pushPrimer.onAllow}
            onSkip={pushPrimer.onSkip}
          />
        ) : null}

        {isPushPhase ? null : (
          <PermissionsPrimerLocationView
            isRequesting={locationPrimer.isRequestingLocationPermission}
            isSkipping={locationPrimer.isSkippingLocationPermission}
            onAllow={locationPrimer.onAllow}
            onSkip={locationPrimer.onSkip}
          />
        )}
      </PermissionsPrompt.ContentContainer>
    </PermissionsPrompt.Container>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  hexagonAnchor: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  appIconAnchor: {
    position: 'absolute',
    top: 460,
    left: 320,
  },
  strokesContainer: {
    position: 'absolute',
    top: '10%',
    left: 0,
    right: 0,
    bottom: '30%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  pushPrimer: ReturnType<typeof usePushNotificationPermissionPrimer>;
  locationPrimer: ReturnType<typeof useLocationPermissionPrimer>;
};
